import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'

export function Card({ title, action, children, className = '' }:
  { title?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-lg border border-slate-200 bg-white ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-3">
          {title && <h2 className="text-sm font-semibold text-slate-900">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  )
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="tnum mt-1.5 text-2xl font-semibold text-slate-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  )
}

/**
 * An on/off switch. A real button with role="switch", so it is reachable by keyboard and
 * announced as a switch, which a styled div never is. Lifted here from BookingMenu so the
 * Payslips total and the booking pages cannot drift into two different-looking switches.
 */
export function Switch({ on, onChange, disabled = false, label }:
  { on: boolean; onChange: (on: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} title={label}
      disabled={disabled} onClick={() => onChange(!on)}
      className={`relative h-5 w-9 shrink-0 rounded-full transition disabled:opacity-50 ${on ? 'bg-emerald-500' : 'bg-slate-300'}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${on ? 'left-[1.125rem]' : 'left-0.5'}`} />
    </button>
  )
}

export function Banner({ kind, children }: { kind: 'error' | 'success' | 'info' | 'warning'; children: ReactNode }) {
  // 'warning' is for something true that needs acting on, as against 'error' for
  // something that went wrong. A released payslip whose figure has since moved is not
  // a failure, but it is not neutral information either.
  const tone = kind === 'error'   ? 'border-red-200 bg-red-50 text-red-800'
             : kind === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
             : kind === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-800'
             :                      'border-slate-200 bg-slate-50 text-slate-700'
  return <div className={`mb-4 rounded-md border px-4 py-2.5 text-sm ${tone}`} role="status">{children}</div>
}

/**
 * `primary` wears the LOCKUP'S OWN GRADIENT, brand-900 to brand-600 — the same two
 * values the mark is drawn from, running the same way — so the button people press is
 * the same brand as the logo above it rather than a flat blue beside it.
 *
 * `danger` is for closing and undoing, not for deleting: a red outline rather than a red
 * fill, so it reads as the way out of a panel without shouting like a destructive action.
 */
/**
 * WHERE A PAGE WOULD OTHERWISE SHOW A RED BANNER OVER AN EMPTY SCREEN.
 *
 * "Nothing here yet" is not a failure and must not be dressed as one. A title that says what
 * is missing, a sentence that says why, and, where the reader can actually do something about
 * it, one thing to press. No icon soup, no apology.
 */
export function EmptyState({ title, children, action }:
  { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
      <div className="mx-auto max-w-md">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {children && <div className="mt-2 text-sm leading-relaxed text-slate-600">{children}</div>}
        {action && <div className="mt-5 flex justify-center">{action}</div>}
      </div>
    </div>
  )
}

export function Button({ variant = 'primary', className = '', ...p }:
  React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' }) {
  const base = 'rounded-md px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50'
  const tone = variant === 'primary'
    ? 'bg-gradient-to-tr from-brand-900 to-brand-600 text-white hover:from-brand-800 hover:to-brand-500'
    : variant === 'danger'
      ? 'border border-rose-300 bg-white text-rose-700 hover:border-rose-400 hover:bg-rose-50'
      : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
  return <button className={`press ${base} ${tone} ${className}`} {...p} />
}

/**
 * Waiting for a page's data. Nothing shows for the first quarter second, so a quick answer
 * goes straight to the content with no "Loading…" flashing past; after that a small ring and
 * the label fade in. The space is held from the start, so nothing jumps when it appears.
 */
export function Spinner({ label = 'Loading…' }: { label?: string }) {
  const [show, setShow] = useState(false)
  useEffect(() => { const t = setTimeout(() => setShow(true), 250); return () => clearTimeout(t) }, [])
  return (
    <div className="py-8 text-center" role="status" aria-live="polite">
      <span className={`inline-flex items-center gap-2 text-sm text-slate-500 transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}>
        <span aria-hidden="true" className="lq-spin h-4 w-4 rounded-full border-2 border-slate-200 border-t-slate-500" />
        {label}
      </span>
    </div>
  )
}


/**
 * A plain modal. Closes on backdrop click and on Escape, and restores page scrolling
 * when it goes away.
 *
 * Deliberately not a focus trap: this is used for read-only detail panels, where
 * trapping focus buys little and a half-built trap is worse than none. If a modal ever
 * holds a form, give it a real one.
 */
export function Modal({ title, onClose, children, size = 'md' }:
  { title: ReactNode; onClose: () => void; children: ReactNode
    /**
     * How wide. 'md' a short confirmation; 'lg' a table; 'xl' something you work
     * IN rather than read — a document beside the fields you are filling from it.
     */
    size?: 'md' | 'lg' | 'xl' }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [onClose])

  /*
   * ⚠️ RENDERED INTO <body>, NOT WHERE IT IS WRITTEN. `position: fixed` is relative to
   * the window only while no ancestor has a transform; one anywhere up the tree makes
   * that ancestor the frame instead. The page wrapper's enter animation left an identity
   * transform behind, so on a page taller than the window this centred itself halfway
   * down the whole document — you got the dimmed backdrop and a panel thousands of
   * pixels below the fold. A portal takes the modal out of reach of all of that.
   */
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         role="dialog" aria-modal="true" onClick={onClose}>
      <div className="lq-overlay absolute inset-0 bg-slate-900/40" />
      <div className={`lq-dialog relative z-10 flex w-full flex-col rounded-lg bg-white shadow-xl ${
             size === 'xl' ? 'h-[92vh] max-w-[1400px]' : 'max-h-[90vh] ' + (size === 'lg' ? 'max-w-5xl' : 'max-w-lg')}`}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-start justify-between border-b border-slate-200 px-4 py-3">
          <div className="min-w-0 text-sm font-semibold text-slate-900">{title}</div>
          <button onClick={onClose} aria-label="Close"
            className="-mr-1 -mt-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            &#10005;
          </button>
        </div>
        {/* The body scrolls, not the page: a long table must not push the header off.
            At 'xl' the body does not scroll as a whole — its panes do, so the
            document stays put while you scroll the form beside it. */}
        <div className={`min-h-0 flex-1 text-sm ${
          size === 'xl' ? 'overflow-hidden' : 'overflow-y-auto px-4 py-3'}`}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}
